import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import Button from "@components/ui/buttons/Button";
import { useState, useRef, useMemo, KeyboardEvent, useEffect } from "react";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import TextField from "@components/ui/textfields/TextField";
import { colors } from "sexy";

import {
  Org,
  User,
  EmailSent,
  ControlPoint,
  Project,
  ProjectDocument,
  EmailTemplate,
  ProjectHistoryEvent,
} from "types";
import { handleFetchEmailOptions, fillVariablesInEmailTemplate, getEmailFromOptionValue, getMatchingPartnerOptionValues } from "helpers/emailHelpers";
import { CustomImage } from "@components/ui/images/CustomImage";
import { getFlutterTime } from "@services/timeHelpers";
import { showNotification } from "context/notification.service";
import { projectsApi, UploadedFile } from "@services/api/projects";

interface EmailChipProps {
  email: string;
  onRemove: () => void;
}

const EmailChip = ({ email, onRemove }: EmailChipProps) => (
  <div className="inline-flex items-center bg-neutral-100 bg-primary-100 text-primary-700 px-3 gap-2 py-0.5 rounded-full text-sm mr-1 mb-1">
    <span>{getEmailFromOptionValue(email)}</span>
    <button
      onClick={onRemove}
      className="hover:bg-primary-200 rounded-full p-0.5 transition-colors"
      type="button"
    >
      <Icon1 name="effacer2" size={18} />
    </button>
  </div>
);

interface EmailInputFieldProps {
  label: string;
  emails: string[];
  onAddEmail: (email: string) => void;
  onRemoveEmail: (index: number) => void;
  setEmails: (emails: string[]) => void;
  placeholder?: string;
  emailOptions: { label: string; value: string }[];
  setEmailOptions: (options: { label: string; value: string }[]) => void;
}

const EmailInputField = ({
  label,
  emails,
  onAddEmail,
  onRemoveEmail,
  setEmails,
  emailOptions,
  setEmailOptions,
  placeholder = "Entrez une adresse email",
}: EmailInputFieldProps) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      validateEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      onAddEmail(trimmedEmail);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      handleAddEmail(inputValue);
    } else if (
      e.key === "Backspace" &&
      inputValue === "" &&
      emails.length > 0
    ) {
      onRemoveEmail(emails.length - 1);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
    if (inputValue.trim()) {
      handleAddEmail(inputValue);
    }
  };

  const filteredOptions = emailOptions.filter(
    (opt) =>
      !emails.includes(opt.value) &&
      (inputValue === "" ||
        opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        opt.value.toLowerCase().includes(inputValue.toLowerCase())),
  );

  return (
    <div className="flex flex-row items-start w-full">
      <div className="min-w-100 pt-2 text-neutral-600">{label}</div>
      <div className="relative w-full">
        <div
          className="w-full border-b-1 border-neutral-300 cursor-text min-h-[32px] flex flex-wrap items-center"
          onClick={() => inputRef.current?.focus()}
        >
          {emails.map((email, index) => (
            <EmailChip
              key={index}
              email={email}
              onRemove={() => onRemoveEmail(index)}
            />
          ))}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={emails.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[150px] border-0 outline-none py-1 text-h2"
          />
        </div>
        {showSuggestions && filteredOptions.length > 0 && (
          <div className="absolute top-full left-0 w-full z-50 bg-white border rounded-lg shadow-lg mt-1 max-h-200 overflow-y-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                className="px-3 py-2 text-h3 hover:bg-neutral-50 cursor-pointer transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAddEmail(option.value);
                  setShowSuggestions(false);
                }}
              >
                <div className="text-black">{getEmailFromOptionValue(option.value)}</div>
                <div className="text-neutral-400 text-h4">{option.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export interface EmailOption {
  label: string;
  value: string;
}

// ─── Photo picker sub-modal ──────────────────────────────────────────────────

const EmailPhotoPickerModal = ({
  activeProject,
  selectedPhotos,
  onConfirm,
  onClose,
}: {
  activeProject: Project;
  selectedPhotos: string[];
  onConfirm: (photoIds: string[]) => void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(selectedPhotos),
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const photos = activeProject.photos || [];

  return (
    <BaseModal
      title="Joindre des photos"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-4 p-4">
        {photos.length > 0 ? (
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((photo) => {
              const isSelected = selected.has(photo.id);
              return (
                <div
                  key={photo.id}
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                    isSelected
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-transparent hover:border-neutral-300"
                  }`}
                  onClick={() => toggle(photo.id)}
                >
                  <CustomImage
                    source={{ uri: photo.file.url }}
                    className="w-24 h-24"
                    hidden={photo.hidden}
                    alt={photo.file.name}
                  />
                  {isSelected && (
                    <div className="absolute top-1 right-1 bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-h3 text-neutral-400 py-2">
            Aucune photo dans ce projet
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={`Joindre${selected.size > 0 ? ` (${selected.size})` : ""}`}
          onClick={() => onConfirm(Array.from(selected))}
        />
      </div>
    </BaseModal>
  );
};

// ─── Project document picker sub-modal ──────────────────────────────────────

interface FlatProjectDoc {
  id: string;
  name: string;
  hidden?: boolean;
  folderPath: string; // "Folder" or "Folder / Sub-folder"
  file: ProjectDocument["file"];
}

function flattenProjectDocuments(project: Project): FlatProjectDoc[] {
  const out: FlatProjectDoc[] = [];
  for (const folder of project.folders || []) {
    for (const doc of folder.documents || []) {
      out.push({
        id: doc.id,
        name: doc.name,
        hidden: doc.hidden,
        folderPath: folder.name,
        file: doc.file,
      });
    }
    for (const sub of folder.sub_folders || []) {
      for (const doc of sub.documents || []) {
        out.push({
          id: doc.id,
          name: doc.name,
          hidden: doc.hidden,
          folderPath: `${folder.name} / ${sub.name}`,
          file: doc.file,
        });
      }
    }
  }
  return out;
}

const EmailProjectDocumentPickerModal = ({
  activeProject,
  selectedDocumentIds,
  onConfirm,
  onClose,
}: {
  activeProject: Project;
  selectedDocumentIds: string[];
  onConfirm: (docIds: string[]) => void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(selectedDocumentIds),
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Group by folderPath for readable listing. Keeps the order they appear
  // in the project so "Documents/Date" stays aligned with this view.
  const docs = useMemo(() => flattenProjectDocuments(activeProject), [activeProject]);
  const groups = useMemo(() => {
    const map = new Map<string, FlatProjectDoc[]>();
    for (const d of docs) {
      const arr = map.get(d.folderPath) || [];
      arr.push(d);
      map.set(d.folderPath, arr);
    }
    return Array.from(map.entries());
  }, [docs]);

  return (
    <BaseModal
      title="Joindre des documents du projet"
      startIcon={<Icon1 name="file" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-4 p-4 max-h-[60vh] overflow-auto">
        {docs.length === 0 ? (
          <div className="text-h3 text-neutral-400 py-2">
            Aucun document dans ce projet
          </div>
        ) : (
          groups.map(([folderPath, groupDocs]) => (
            <div key={folderPath} className="flex flex-col gap-1">
              <div className="text-h4 text-neutral-400 font-semibold">
                {folderPath}
              </div>
              {groupDocs.map((doc) => {
                const isSelected = selected.has(doc.id);
                return (
                  <div
                    key={doc.id}
                    onClick={() => toggle(doc.id)}
                    className={`flex flex-row items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                      isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-transparent hover:bg-neutral-50"
                    }`}
                  >
                    <Icon1 name="file" size={18} color={colors.neutral_400} />
                    <div className="flex-1 text-sm truncate">{doc.name}</div>
                    {isSelected && (
                      <div className="bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={`Joindre${selected.size > 0 ? ` (${selected.size})` : ""}`}
          onClick={() => onConfirm(Array.from(selected))}
        />
      </div>
    </BaseModal>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

interface EmailModalProps {
  onClose: () => void;
  email_template_id?: string;
  controlpoint?: ControlPoint;
  refreshControlPoint?: () => void;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: User;
  orgData?: Org;
  sentEmails?: EmailSent[];
  setSentEmails?: (emails: EmailSent[]) => void;
  isGuest?: boolean;
  initialToEmails?: string[];
  initialSubject?: string;
  initialBody?: string;
}

export const EmailModal = ({
  onClose,
  email_template_id,
  controlpoint,
  refreshControlPoint,
  activeProject,
  setActiveProject,
  userData,
  orgData,
  sentEmails = [],
  setSentEmails,
  isGuest = false,
  initialToEmails,
  initialSubject,
  initialBody,
}: EmailModalProps) => {
  const fetchSentEmails = useProjectStore((state) => state.fetchSentEmails);
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);
  const orgMembers = useDataStore((state) => state.orgMembers);

  const defaultSignature = userData && orgData
    ? `${userData.infos.display_name}\n${orgData.infos.name}`
    : "";
  const defaultBody = `Bonjour,\n\n\n\n${defaultSignature}`;

  const initialTemplateId = email_template_id || "";
  const initialTemplate = initialTemplateId ? emailTemplates.find((m) => m.id === initialTemplateId) : null;
  const initialFilled =
    initialTemplate && activeProject && userData && orgData
      ? fillVariablesInEmailTemplate(initialTemplate as EmailTemplate, activeProject, userData, orgData)
      : null;

  const [model, setModel] = useState<string>(initialTemplateId);
  const [showCC, setShowCC] = useState<boolean>(false);
  const [showBCC, setShowBCC] = useState<boolean>(false);
  // Guard against double-click / rapid re-submit. Reset on unmount so a
  // failed send followed by a new edit cycle isn't permanently blocked.
  const [isSending, setIsSending] = useState<boolean>(false);
  // Stable idempotency id for this modal session. Every retry from the
  // same modal reuses this id so the backend short-circuits the second
  // call as a duplicate instead of re-sending.
  const [sendAttemptId] = useState<string>(
    () => `email_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  );

  const [toEmails, setToEmails] = useState<string[]>(initialToEmails || []);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [emailOptions, setEmailOptions] = useState<EmailOption[]>([]);

  const [subject, setSubject] = useState<string>(
    initialSubject ?? (initialFilled?.subject || initialTemplate?.subject || ""),
  );
  const [content, setContent] = useState<string>(
    initialBody ?? (initialFilled?.body || initialTemplate?.body || defaultBody),
  );
  const [photos, setPhotos] = useState<string[]>([]);
  const [documents, setDocuments] = useState<string[]>(
    emailTemplates.find((m) => m.id === model)?.library_documents_id || [],
  );
  const [addPhotoModalOpen, setAddPhotoModalOpen] = useState(false);
  const [addProjectDocModalOpen, setAddProjectDocModalOpen] = useState(false);
  const [projectDocumentIds, setProjectDocumentIds] = useState<string[]>([]);
  const [joinMenuOpen, setJoinMenuOpen] = useState(false);
  // Files picked from the user's computer: uploaded to GCS on pick so
  // handleSend has nothing async to wait on.
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    // Reset the input so picking the same file twice in a row still fires
    // an onChange event.
    e.target.value = "";
    if (picked.length === 0 || !activeProject) return;
    setIsUploadingFile(true);
    try {
      const uploaded = await projectsApi.uploadFiles(activeProject.id, picked);
      setUploadedFiles((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error("Failed to upload email attachment:", err);
      showNotification({
        type: "error",
        message: "Impossible d'ajouter la pièce jointe",
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const removeUploadedFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Resolve the selected project-document ids to their File descriptors so
  // the send payload can treat them like any other attachment.
  const selectedProjectDocs = useMemo(() => {
    if (!activeProject || projectDocumentIds.length === 0) return [];
    return flattenProjectDocuments(activeProject).filter((d) =>
      projectDocumentIds.includes(d.id),
    );
  }, [activeProject, projectDocumentIds]);

  const removeProjectDoc = (id: string) => {
    setProjectDocumentIds((prev) => prev.filter((x) => x !== id));
  };

  const addToEmail = (email: string) => setToEmails([...toEmails, email]);
  const removeToEmail = (index: number) =>
    setToEmails(toEmails.filter((_, i) => i !== index));

  const addCcEmail = (email: string) => setCcEmails([...ccEmails, email]);
  const removeCcEmail = (index: number) =>
    setCcEmails(ccEmails.filter((_, i) => i !== index));

  const addBccEmail = (email: string) => setBccEmails([...bccEmails, email]);
  const removeBccEmail = (index: number) =>
    setBccEmails(bccEmails.filter((_, i) => i !== index));

  // Pre-selection of partner recipients must happen exactly once. ProjectScreen
  // polls the project every 30s and replaces its object reference, which
  // re-runs this effect; without this guard the re-run called setToEmails again
  // and wiped recipients the user had manually chosen mid-compose (the reported
  // "I lost the destinataires after joining a file" bug).
  const didPreselectRecipientsRef = useRef(false);
  useEffect(() => {
    if (!activeProject) return;
    if (isGuest) {
      // Guest: only show project team members as recipients
      const team = activeProject.infos.team;
      const teamIds = [team.conductor_id, team.commercial_id, team.comptability_id].filter(Boolean) as string[];
      const teamOptions: EmailOption[] = [];
      for (const memberId of teamIds) {
        const member = orgMembers.find((m) => m.id === memberId);
        if (member?.infos?.email) {
          const role = memberId === team.conductor_id ? "conducteur" : memberId === team.commercial_id ? "commercial" : "comptable";
          teamOptions.push({
            label: `${member.infos.display_name || member.infos.email} (${role})`,
            value: member.infos.email,
          });
        }
      }
      setEmailOptions(teamOptions);
      return;
    }

    // Recomputing options on each project refresh is safe — it never touches
    // the user's selected recipients.
    const options = handleFetchEmailOptions(activeProject);
    setEmailOptions(options);

    // Pre-select partner emails ONLY on first load, never on subsequent
    // background refreshes (see didPreselectRecipientsRef above).
    if (
      !didPreselectRecipientsRef.current &&
      initialTemplate &&
      initialTemplate.destination_type === "partner" &&
      initialTemplate.partner_company_types &&
      initialTemplate.partner_company_types.length > 0
    ) {
      const matchingValues = getMatchingPartnerOptionValues(
        options,
        activeProject.infos.partners,
        initialTemplate.partner_company_types,
      );
      setToEmails(matchingValues);
    }
    didPreselectRecipientsRef.current = true;
  }, [activeProject, isGuest]);

  const handleModelChange = (value: string) => {
    setModel(value);

    if (!value) {
      setSubject("");
      setContent(defaultBody);
      setDocuments([]);
      setToEmails([]);
      return;
    }

    const selectedModel = emailTemplates.find((m) => m.id === value);
    if (selectedModel && activeProject && userData && orgData) {
      const filled = fillVariablesInEmailTemplate(
        selectedModel as EmailTemplate,
        activeProject,
        userData,
        orgData,
      );

      setSubject(filled.subject || "");
      setContent(filled.body || "");
      setDocuments(selectedModel.library_documents_id || []);

      // Pre-select partner emails matching the template's company types
      if (
        selectedModel.destination_type === "partner" &&
        selectedModel.partner_company_types &&
        selectedModel.partner_company_types.length > 0
      ) {
        const options = handleFetchEmailOptions(activeProject);
        const matchingValues = getMatchingPartnerOptionValues(
          options,
          activeProject.infos.partners,
          selectedModel.partner_company_types,
        );
        setToEmails(matchingValues);
      }
    }
  };

  const handleSend = async () => {
    if (!activeProject || !userData || !orgData) return;
    // Hard guard against double-send: a slow backend + an impatient user
    // clicking Envoyer twice was the most likely cause of the reported
    // "emails sont envoyés deux fois" bug. setIsSending acts as both a
    // UI disable and a re-entry lock — the second click returns before
    // the API call even starts.
    if (isSending) return;
    setIsSending(true);

    try {
      // Backend now handles email sending, history event creation, and controlpoint update
      const updatedProject = await projectsApi.sendEmailWithHistory(
        activeProject.id,
        {
          id: sendAttemptId,
          subject: subject,
          body: content,
          org_id: orgData.id,
          project_id: activeProject.id,
          sender_user_id: userData.id,
          email_template_id: model,
          emails_to: Array.from(new Set(toEmails.map(getEmailFromOptionValue))),
          emails_cc: Array.from(new Set(ccEmails.map(getEmailFromOptionValue))),
          emails_bcc: Array.from(new Set(bccEmails.map(getEmailFromOptionValue))),
          files: [
            ...libraryDocuments
              .filter((doc) => documents.includes(doc.id))
              .map((doc) => doc.file),
            ...selectedProjectDocs.map((d) => d.file),
            ...uploadedFiles.map((f) => ({
              id: f.id,
              name: f.name,
              url: f.url,
              octets: f.size,
              mimeType: f.content_type,
            })),
          ],
          photos_id: photos,
          controlpoint_id: controlpoint?.id,
        }
      );

      // Refresh sent emails list
      await fetchSentEmails(orgData.id, activeProject.id);

      // Update local state with the new project
      if (setActiveProject) {
        setActiveProject(updatedProject);
      }

      if (refreshControlPoint) {
        refreshControlPoint();
      }

      showNotification({
        type: "success",
        message: `Email envoyé à ${toEmails.map(getEmailFromOptionValue).join(", ")}`,
      });

      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'email",
      });
    } finally {
      setIsSending(false);
    }
  };

  const selectedModel = emailTemplates.find((m) => m.id === model);
  const hasUploadedImage = uploadedFiles.some((f) =>
    f.content_type?.startsWith("image/"),
  );
  const isSendDisabled =
    (selectedModel?.mandatory_photo &&
      photos.length === 0 &&
      !hasUploadedImage) ||
    toEmails.length === 0;

  return (
    <>
      {/* Photo picker is rendered as a SIBLING of the main BaseModal,
          not as a child. Both use createPortal to body, and keeping them
          as siblings in the React tree makes the stacking order clean —
          otherwise the outer modal's overlay can intercept clicks meant
          for the picker's Joindre button depending on React's reconcile
          order. This was the actual bug behind "Joindre ne fait rien". */}
      {addProjectDocModalOpen && activeProject && (
        <EmailProjectDocumentPickerModal
          activeProject={activeProject}
          selectedDocumentIds={projectDocumentIds}
          onConfirm={(ids) => {
            setProjectDocumentIds(ids);
            setAddProjectDocModalOpen(false);
          }}
          onClose={() => setAddProjectDocModalOpen(false)}
        />
      )}
      {addPhotoModalOpen && activeProject && (
        <EmailPhotoPickerModal
          activeProject={activeProject}
          selectedPhotos={photos}
          onConfirm={(photoIds) => {
            setPhotos(photoIds);
            setAddPhotoModalOpen(false);
          }}
          onClose={() => setAddPhotoModalOpen(false)}
        />
      )}
    <BaseModal
      width="max-w-800"
      title="Envoyer un email"
      startIcon={<Icon1 name="email" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        {!isGuest && (
          <DropDown
            options={[
              { value: "", label: "Aucun" },
              // Hide templates the user marked `hidden_from_suggestions`,
              // unless it's the currently-selected model (otherwise the
              // dropdown would render an empty label).
              ...emailTemplates
                .filter((r) => !r.hidden_from_suggestions || r.id === model)
                .map((r) => ({
                  value: r.id,
                  label: r.name,
                })),
            ]}
            label="Modèle d'email"
            value={model}
            onSelect={(value) => handleModelChange(value as string)}
          />
        )}

        <div className="flex flex-col text-h2 py-3 gap-2">
          {/* From */}
          {!isGuest && (
            <div className="flex flex-row items-center">
              <div className="min-w-100 text-neutral-600">De</div>
              <div className="w-full py-2 font-medium border-b-1">
                {orgData?.infos?.sender_email || "email@example.com"}
              </div>
            </div>
          )}

          {/* To */}
          <div className="flex flex-col">
            <div className="flex flex-row items-start">
              <EmailInputField
                label="Vers"
                emails={toEmails}
                onAddEmail={addToEmail}
                setEmails={setToEmails}
                onRemoveEmail={removeToEmail}
                placeholder="Ajouter des destinataires"
                emailOptions={emailOptions}
                setEmailOptions={setEmailOptions}
              />
              {!isGuest && (
                <div className="ml-2 flex flex-row gap-2 pt-2">
                  <button
                    className="text-sm px-2 py-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100"
                    onClick={() => {
                      // Select all partner emails
                      const partnerValues = emailOptions
                        .filter((opt) => opt.value.startsWith("partner_"))
                        .map((opt) => opt.value);
                      setToEmails((prev) => Array.from(new Set([...prev, ...partnerValues])));
                    }}
                    type="button"
                  >
                    Tous
                  </button>
                  {!showCC && (
                    <button
                      className="text-sm px-2 py-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100"
                      onClick={() => setShowCC(true)}
                      type="button"
                    >
                      CC
                    </button>
                  )}
                  {!showBCC && (
                    <button
                      className="text-sm px-2 py-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100"
                      onClick={() => setShowBCC(true)}
                      type="button"
                    >
                      BCC
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CC */}
          {showCC && (
            <EmailInputField
              label="CC"
              emails={ccEmails}
              onAddEmail={addCcEmail}
              setEmails={setCcEmails}
              onRemoveEmail={removeCcEmail}
              placeholder="Copie"
              emailOptions={emailOptions}
              setEmailOptions={setEmailOptions}
            />
          )}

          {/* BCC */}
          {showBCC && (
            <EmailInputField
              label="BCC"
              emails={bccEmails}
              onAddEmail={addBccEmail}
              setEmails={setBccEmails}
              onRemoveEmail={removeBccEmail}
              placeholder="Copie cachée"
              emailOptions={emailOptions}
              setEmailOptions={setEmailOptions}
            />
          )}

          {/* Subject */}
          <div className="flex flex-row items-center mt-5">
            <div className="min-w-100 text-neutral-600">Sujet</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border-0 border-b-1 border-neutral-300 rounded-none py-2 outline-none focus:border-primary-500 transition-colors"
            />
          </div>
        </div>

        <AreaTextField
          label="Contenu"
          rows={12}
          value={content}
          onChange={(val) => setContent(val)}
          placeholder={`Bonjour,

Dans le cadre du suivi de l'opération, nous avons procédé à la vérification des factures transmises par les artisans au regard des devis validés.

Nous vous remercions de bien vouloir corriger la facture concernée afin qu'elle soit conforme au devis contractuel, puis de nous la transmettre à nouveau pour validation.

Nous restons à votre disposition pour toute précision complémentaire et pour échanger si nécessaire sur ce point.

Cordialement,

David Comy
Bureau Gilbert Comy`}
        />

        {/* Photos Preview (non-guest only) */}
        {!isGuest && photos.length > 0 && (
          <div
            className="flex flex-row gap-1.5 mt-2 flex-wrap items-center p-2 bg-neutral-50 rounded-lg cursor-pointer"
            onClick={() => setAddPhotoModalOpen(true)}
          >
            {activeProject?.photos
              ?.filter((p) => photos.includes(p.id))
              .map((photo, index) => (
                <CustomImage
                  key={index}
                  source={{ uri: photo.file.url }}
                  alt={`Photo ${index + 1}`}
                  className="w-20 h-20 rounded-lg"
                  hidden={photo.hidden}
                />
              ))}
          </div>
        )}

        {/* Mandatory Photo Warning (non-guest only) */}
        {!isGuest && selectedModel?.mandatory_photo && photos.length === 0 && !hasUploadedImage && (
          <div
            className="w-full flex flex-row p-2.5 rounded-lg border-2 justify-center cursor-pointer"
            style={{
              backgroundColor: colors.red_100,
              borderColor: colors.red_300,
            }}
            onClick={() => setAddPhotoModalOpen(true)}
          >
            <div className="text-sm" style={{ color: colors.red_500 }}>
              Ce modèle d'email nécessite l'ajout d'au moins une photo.
            </div>
          </div>
        )}

        {/* Document Attachments (non-guest only) */}
        {!isGuest && documents.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            {libraryDocuments
              ?.filter((doc) => documents.includes(doc.id))
              .map((doc, index) => (
                <div
                  key={index}
                  className="flex flex-row gap-2 items-center p-2 bg-neutral-50 rounded-lg"
                >
                  <Icon1 name="file" size={20} color={colors.neutral_400} />
                  <div
                    className="flex-1 text-sm truncate"
                    style={{ color: colors.neutral_500 }}
                  >
                    {doc.file.name}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Project document attachments */}
        {!isGuest && selectedProjectDocs.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            {selectedProjectDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex flex-row gap-2 items-center p-2 bg-neutral-50 rounded-lg"
              >
                <Icon1 name="file" size={20} color={colors.neutral_400} />
                <div
                  className="flex-1 text-sm truncate"
                  style={{ color: colors.neutral_500 }}
                >
                  {doc.name}
                  <span className="text-xs text-neutral-400 ml-2">
                    {doc.folderPath}
                  </span>
                </div>
                <button
                  onClick={() => removeProjectDoc(doc.id)}
                  className="hover:bg-neutral-200 rounded-full p-0.5 transition-colors"
                  title="Retirer"
                >
                  <Icon1 name="effacer2" size={16} color={colors.neutral_400} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Computer-picked attachments */}
        {!isGuest && uploadedFiles.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex flex-row gap-2 items-center p-2 bg-neutral-50 rounded-lg"
              >
                <Icon1 name="file" size={20} color={colors.neutral_400} />
                <div
                  className="flex-1 text-sm truncate"
                  style={{ color: colors.neutral_500 }}
                >
                  {file.name}
                </div>
                <button
                  onClick={() => removeUploadedFile(file.id)}
                  className="hover:bg-neutral-200 rounded-full p-0.5 transition-colors"
                  title="Retirer"
                >
                  <Icon1 name="effacer2" size={16} color={colors.neutral_400} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          // Mirror the backend allow-list (ALLOWED_FILE_TYPES in
          // project_router.py) so the OS dialog doesn't hide images.
          accept="image/*,application/pdf,application/xml,text/xml,application/zip"
          className="hidden"
          onChange={handleFilePick}
        />
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        {!isGuest && (
          <div className="relative">
            <SecondaryButton
              variant="small"
              onClick={() => setJoinMenuOpen((v) => !v)}
              disabled={isUploadingFile}
            >
              <>
                {isUploadingFile ? "Envoi..." : "Joindre"}
                <Icon1 name="join" size={18} />
              </>
            </SecondaryButton>
            {joinMenuOpen && (
              <>
                {/* click-outside overlay */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setJoinMenuOpen(false)}
                />
                {/* menu opens upward so it stays inside the viewport
                    (footer sits at the bottom of the modal) */}
                <div className="absolute bottom-full right-0 mb-2 z-50 w-56 bg-white border border-neutral-200 rounded-lg shadow-xl overflow-hidden">
                  <button
                    className="flex flex-row items-center gap-3 w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
                    onClick={() => {
                      setJoinMenuOpen(false);
                      setAddPhotoModalOpen(true);
                    }}
                  >
                    <Icon1 name="image" size={18} color={colors.neutral_500} />
                    <span className="text-sm text-neutral-800">Photos du projet</span>
                  </button>
                  <button
                    className="flex flex-row items-center gap-3 w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
                    onClick={() => {
                      setJoinMenuOpen(false);
                      setAddProjectDocModalOpen(true);
                    }}
                  >
                    <Icon1 name="folder" size={18} color={colors.neutral_500} />
                    <span className="text-sm text-neutral-800">Documents du projet</span>
                  </button>
                  <button
                    className="flex flex-row items-center gap-3 w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
                    onClick={() => {
                      setJoinMenuOpen(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    <Icon1 name="upload" size={18} color={colors.neutral_500} />
                    <span className="text-sm text-neutral-800">Depuis l'ordinateur</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <Button
          variant="small"
          label={isSending ? "Envoi en cours..." : "Envoyer"}
          onClick={handleSend}
          disabled={isSendDisabled || isSending}
        />
      </div>
    </BaseModal>
    </>
  );
};
